import React, { useEffect, useMemo, useRef, useState } from "react"
import BarChart from "./Barchart"
import AreaChart from "./Areachart"
import ServiceLevelChart from "./ServiceLevelChart"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap"
import { FaFilter } from "react-icons/fa"
import Select from "react-select"
import { customStyles } from "utils/SelectStyles"
import { FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import { DatePicker } from "antd"
import { months } from "utils/defaultpositions"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"
import { MdDownload } from "react-icons/md"
import Swal from "sweetalert2"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import dayjs from "dayjs"

const { RangePicker } = DatePicker

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 2020 }, (_, i) => ({
  label: `${2021 + i}`,
  value: 2021 + i,
}))?.reverse()

const pivotToMultiSeries = (
  rows = [],
  dateKey,
  nameKey,
  valueKey,
  maxSeries
) => {
  if (!rows.length) return { categories: [], series: [] }

  // Unique sorted dates → x-axis categories
  const dateSet = [...new Set(rows.map(r => r[dateKey]))].sort()
  const categories = dateSet.map(d => moment(d).format("DD-MM-YYYY"))

  // Unique entity names, capped at maxSeries
  const names = [...new Set(rows.map(r => r[nameKey]))].slice(0, maxSeries)

  const series = names.map(name => {
    // Build a lookup: date → value for this entity
    const lookup = {}
    rows
      .filter(r => r[nameKey] === name)
      .forEach(r => {
        lookup[r[dateKey]] = Number(r[valueKey])
      })

    return {
      name,
      data: dateSet.map(d => lookup[d] ?? 0),
    }
  })

  return { categories, series }
}

const SalesRevenue = () => {
  const [loading, setLoading] = useState(false)
  const [revenueData, setRevenueData] = useState({})
  const [dateRange, setDateRange] = useState([])
  const [month, setMonth] = useState([])
  const [year, setYear] = useState([])

  const [currentweek, setCurrentWeek] = useState([])
  const [maxDate, setMaxDate] = useState("")

  const [drillModal, setDrillModal] = useState(false)
  const [drillCustomer, setDrillCustomer] = useState(null)
  const [drillData, setDrillData] = useState([])
  const [drillLoading, setDrillLoading] = useState(false)

  const [catModal, setCatModal] = useState(false)
  const [catDrillTitle, setCatDrillTitle] = useState("")
  const [catDrillData, setCatDrillData] = useState([])
  const [catDrillLoading, setCatDrillLoading] = useState(false)

  const [pipelineModal, setPipelineModal] = useState(false)
  const [pipelineTitle, setPipelineTitle] = useState("")
  const [pipelineData, setPipelineData] = useState([])
  const [pipelineLoading, setPipelineLoading] = useState(false)

  useEffect(() => {
    const Years = secureLocalStorage?.getItem("Years")
    const Months = secureLocalStorage?.getItem("Months")
    const DateRange = secureLocalStorage?.getItem("DateRange")
    const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")

    setYear(Years)
    setMonth(Months)

    const activeDateRaw = DateRange ?? FinalRangeRaw
    setDateRange(activeDateRaw ? activeDateRaw.map(d => dayjs(d)) : [])
    setMaxDate(FinalRangeRaw?.[1] ? dayjs(FinalRangeRaw[1]) : null)
    setCurrentWeek(activeDateRaw ? activeDateRaw.map(d => dayjs(d)) : [])

    const fetchInitialData = async () => {
      const activeDateRange = activeDateRaw
        ? activeDateRaw.map(d => dayjs(d))
        : []

      const payload = {
        startDate: activeDateRange?.[0]?.isValid()
          ? activeDateRange[0].format("YYYY-MM-DD")
          : undefined,
        endDate: activeDateRange?.[1]?.isValid()
          ? activeDateRange[1].format("YYYY-MM-DD")
          : undefined,
        month: Months?.map(i => i?.value)?.join(",") || undefined,
        year: Years?.map(i => i?.value)?.join(",") || undefined,
      }

      const APidata = await FetchPostData(
        setLoading,
        "sheet/sales/revenue",
        payload
      )

      if (APidata?.data?.success) setRevenueData(APidata.data)
      else setRevenueData({})
    }

    fetchInitialData()
  }, [])

  const handleFilter = async () => {
    const APidata = await FetchPostData(setLoading, "sheet/sales/revenue", {
      startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      month: month?.map(i => i?.value)?.join(","),
      year: year?.map(i => i?.value)?.join(","),
    })
    if (APidata?.data?.success) setRevenueData(APidata.data)
    else setRevenueData({})
  }

  // ── Bar chart data ──────────────────────────────────────────────────────

  const topCustomerBar = useMemo(() => {
    const data = revenueData?.top10customers || []
    return {
      categories: data.map(i => i.customerName),
      series: [
        {
          name: "Revenue",

          data: data.map(i => Number(i.totalWeeklySalesValue)),
        },
      ],
    }
  }, [revenueData])

  const topProductBar = useMemo(() => {
    const data = [...(revenueData?.revenueByCategory || [])].sort(
      (a, b) =>
        Number(b.totalWeeklySalesValue) - Number(a.totalWeeklySalesValue)
    )
    return {
      categories: data.map(i => i.categoryName),
      series: [
        {
          name: "Revenue",
          data: data.map(i => Number(i.totalWeeklySalesValue)),
        },
      ],
    }
  }, [revenueData])

  const topsProductBar = useMemo(() => {
    const data = revenueData?.topSproducts || []
    return {
      categories: data.map(i => i.productName),
      series: [
        { name: "Top Products", data: data.map(i => Number(i.totalSellCost)) },
      ],
    }
  }, [revenueData])

  const topsausageCat = useMemo(() => {
    const data = [...(revenueData?.revenueBySausageCategory || [])].sort(
      (a, b) =>
        Number(b.totalWeeklySalesValue) - Number(a.totalWeeklySalesValue)
    )
    return {
      categories: data.map(i => i.categoryName),
      series: [
        {
          name: "Top Sausage Category",
          data: data.map(i => Number(i.totalWeeklySalesValue)),
        },
      ],
    }
  }, [revenueData])

  // name → code lookups so onBarClick (which passes the label) resolves to the correct code
  const categoryCodeMap = useMemo(() => {
    const map = {}
    ;(revenueData?.revenueByCategory || []).forEach(i => {
      map[i.categoryName] = i.category
    })
    return map
  }, [revenueData])

  const sausageCategoryCodeMap = useMemo(() => {
    const map = {}
    ;(revenueData?.revenueBySausageCategory || []).forEach(i => {
      map[i.categoryName] = i.subCategory
    })
    return map
  }, [revenueData])

  const salespipeline = useMemo(() => {
    const data = revenueData?.salesPipeline || []
    return {
      categories: data.map(i => moment(i.date).format("DD-MM-YYYY")),
      series: [
        { name: "Comments", data: data.map(i => Number(i.totalComments)) },
      ],
    }
  }, [revenueData])

  // formatted label → raw ISO date for pipeline click handler
  const pipelineDateMap = useMemo(() => {
    const map = {}
    ;(revenueData?.salesPipeline || []).forEach(i => {
      map[moment(i.date).format("DD-MM-YYYY")] = moment(i.date).format(
        "YYYY-MM-DD"
      )
    })
    return map
  }, [revenueData])

  // ── Multi-line area chart data ──────────────────────────────────────────

  /**
   * Revenue by Customer — 10 lines
   * Expected API shape: [{ date, customerName, totalSellCost }, ...]
   * API key: revenueData.topCustomers
   */
  const topCustomerTrend = useMemo(() => {
    return pivotToMultiSeries(
      revenueData?.revenueBytop10CustomersWeekly || [],
      "date",
      "customerName",
      "totalWeeklySalesValue",
      10 // max 10 customer lines
    )
  }, [revenueData])

  /**
   * Revenue by Category — 5 lines
   * Expected API shape: [{ date, categoryName, totalSellCost }, ...]
   * API key: revenueData.topProducts
   */
  const topProductTrend = useMemo(() => {
    return pivotToMultiSeries(
      revenueData?.revenueByCategoriesWeekly || [],
      "date",
      "categoryName", // change to "productName" if that is what API returns
      "totalWeeklySalesValue",
      5 // max 5 category lines
    )
  }, [revenueData])

  const handleCustomerClick = async customerName => {
    const customer = (revenueData?.top10customers || []).find(
      c => c.customerName === customerName
    )
    const customerCode = customer?.customerCode

    setDrillCustomer(customerName)
    setDrillModal(true)
    setDrillData([])
    setDrillLoading(true)

    const isOthers = customerCode === "OTHERS"
    const otherCodes = isOthers
      ? (revenueData?.top10customers || [])
          .slice(0, -1)
          .map(c => c.customerCode)
          .filter(Boolean)
      : undefined

    const res = await FetchPostData(setDrillLoading, "sheet/customer/data", {
      customerCode: isOthers ? "OTHERS" : customerCode,
      ...(otherCodes ? { existingCodes: otherCodes } : {}),
      startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      month: month?.map(i => i?.value)?.join(","),
      year: year?.map(i => i?.value)?.join(","),
    })

    const raw = res?.data?.customerData || res?.data?.otherCustomersData || []
    setDrillData(Array.isArray(raw) ? raw : Object.values(raw))
    setDrillLoading(false)
  }

  const openCatDrill = async (title, code, endpoint) => {
    setCatDrillTitle(title)
    setCatModal(true)
    setCatDrillData([])
    setCatDrillLoading(true)
    const res = await FetchPostData(
      setCatDrillLoading,
      `${endpoint}/${encodeURIComponent(code)}`,
      {
        startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
        endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
        month: month?.map(i => i?.value)?.join(","),
        year: year?.map(i => i?.value)?.join(","),
      }
    )
    const raw = res?.data?.data || res?.data || []
    setCatDrillData(Array.isArray(raw) ? raw : Object.values(raw))
    setCatDrillLoading(false)
  }

  const handleCategoryClick = name =>
    openCatDrill(
      name,
      categoryCodeMap[name] ?? name,
      "sheet/customers/by/category"
    )

  const handleSausageCategoryClick = name =>
    openCatDrill(
      name,
      sausageCategoryCodeMap[name] ?? name,
      "sheet/customers/by/subcategory"
    )

  const handlePipelineClick = async formattedDate => {
    const rawDate = pipelineDateMap[formattedDate] ?? formattedDate
    setPipelineTitle(formattedDate)
    setPipelineModal(true)
    setPipelineData([])
    setPipelineLoading(true)
    const res = await FetchPostData(
      setPipelineLoading,
      "sheet/pipeline/tracker",
      {
        startDate: rawDate,
        endDate: rawDate,
      }
    )
    const raw = res?.data?.data || res?.data || []
    setPipelineData(Array.isArray(raw) ? raw : Object.values(raw))
    setPipelineLoading(false)
  }

  const serviceLevelData = revenueData?.serviceLevel || []
  const serviceLevelPrevData = revenueData?.secondYearServiceLevel || []

  // ── PDF export ──────────────────────────────────────────────────────────

  const dashboardRef = useRef(null)

  const handleExport = async () => {
    const element = dashboardRef.current
    if (!element) return

    try {
      setLoading(true)

      const originalHeight = element.style.height
      const originalOverflow = element.style.overflow
      element.style.height = "auto"
      element.style.overflow = "visible"

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      const canvas = await html2canvas(element, {
        scale: 1.2,
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: -scrollTop,
        y: 0,
        x: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        logging: false,
      })

      element.style.height = originalHeight
      element.style.overflow = originalOverflow

      const imgData = canvas.toDataURL("image/jpeg", 0.75)
      const imgWidth = canvas.width / 1.2
      const imgHeight = canvas.height / 1.2
      const marginLeft = 30
      const marginRight = 30
      const marginTop = 10
      const marginBottom = 20
      const pdfWidth = imgWidth + marginLeft + marginRight
      const pdfHeight = imgHeight + marginTop + marginBottom

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
        unit: "px",
        format: [pdfWidth, pdfHeight],
        compress: true,
      })

      pdf.addImage(
        imgData,
        "JPEG",
        marginLeft,
        marginTop,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      )

      pdf.save(
        `Sales_Revenue_${new Date()
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-")}.pdf`
      )
    } catch (e) {
      console.log(e)
      Swal.fire({
        title: "Export Failed",
        text: "Something went wrong while exporting.",
        icon: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page-content" ref={dashboardRef}>
      {loading && <Loader />}

      {/* ── Filter Header ──────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
          borderRadius: "16px",
          border: "1px solid #ede9fe",
          boxShadow: "0 4px 24px rgba(108,99,255,0.07)",
          padding: "16px 20px",
          marginBottom: "24px",
          position: "relative",
          overflow: "visible",
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#000",
                }}
              >
                Sales Revenue
              </h4>
              <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
                Revenue performance & trends
              </p>
            </div>
            <div
              style={{ width: "1px", height: "36px", background: "#e2e8f0" }}
            />

            {/* Export button */}
            <Button
              onClick={handleExport}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "#0f172a",
                border: "none",
                borderRadius: "10px",
                padding: "8px 16px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
                cursor: "pointer",
                letterSpacing: "0.2px",
              }}
            >
              <MdDownload size={15} />
              Export
            </Button>
            {currentweek?.[0] && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  padding: "6px 12px",
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#16a34a",
                    display: "inline-block",
                    boxShadow: "0 0 0 2px #bbf7d0",
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#15803d",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    Latest week Upload
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#166534",
                      fontWeight: 600,
                      marginTop: "1px",
                    }}
                  >
                    {currentweek[0].format("DD MMM")} —{" "}
                    {currentweek[1].format("DD MMM YYYY")}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <RangePicker
              value={dateRange}
              placeholder={["Start (Sat)", "End (Fri)"]}
              onChange={setDateRange}
              format="DD-MM-YYYY"
              style={{ borderRadius: "8px", height: "38px" }}
              disabledDate={current => {
                return current && current > maxDate.endOf("day")
              }}
            />
            <Select
              isMulti
              options={months}
              value={month}
              onChange={setMonth}
              styles={customStyles}
              placeholder="Month"
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Select
              isMulti
              options={years}
              value={year}
              onChange={setYear}
              styles={customStyles}
              placeholder="Year"
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Button onClick={handleFilter} className="btn-blue rounded-2">
              <FaFilter size={12} />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Bar Charts ─────────────────────────────────────────────── */}
      <Row>
        <Col md="6">
          <BarChart
            title="Revenue By Customer"
            categories={topCustomerBar.categories}
            series={topCustomerBar.series}
            horizontal
            pound
            onBarClick={handleCustomerClick}
          />
        </Col>
        <Col md="6">
          <BarChart
            title="Revenue By Category"
            categories={topProductBar.categories}
            series={topProductBar.series}
            horizontal
            pound
            onBarClick={handleCategoryClick}
          />
        </Col>
        <Col md="6">
          <BarChart
            title="Revenue By Sausage Category"
            categories={topsausageCat.categories}
            series={topsausageCat.series}
            horizontal
            pound
            onBarClick={handleSausageCategoryClick}
          />
        </Col>
        <Col md="6">
          <BarChart
            title="Sales Pipeline"
            categories={salespipeline.categories}
            series={salespipeline.series}
            horizontal
            onBarClick={handlePipelineClick}
          />
        </Col>
      </Row>

      {/* ── Area Charts ────────────────────────────────────────────── */}
      <Row className="mt-2">
        {/* 10-line multi-series chart */}
        <Col md="6">
          <AreaChart
            title="Revenue by Customer"
            categories={topCustomerTrend.categories}
            series={topCustomerTrend.series}
            pound
            colorIndex={0}
          />
        </Col>

        {/* 5-line multi-series chart */}
        <Col md="6">
          <AreaChart
            title="Revenue By  Category"
            categories={topProductTrend.categories}
            series={topProductTrend.series}
            pound
            colorIndex={0}
          />
        </Col>

        <Col md="12">
          <ServiceLevelChart
            data={serviceLevelData}
            previousData={serviceLevelPrevData}
          />
        </Col>
      </Row>
      {/* ── Customer Drill-Down Modal ──────────────────────────── */}
      <Modal
        isOpen={drillModal}
        toggle={() => setDrillModal(false)}
        size="lg"
        centered
      >
        <ModalHeader
          toggle={() => setDrillModal(false)}
          style={{ borderBottom: "1px solid #f1f5f9", padding: "14px 20px" }}
        >
          <span style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
            {drillCustomer} — Revenue Breakdown
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: 0 }}>
          {drillLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "48px",
              }}
            >
              <Spinner color="primary" />
            </div>
          ) : drillData.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px",
                color: "#94a3b8",
                fontSize: "14px",
              }}
            >
              No data available
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              {(() => {
                const grouped = drillData.reduce((acc, row) => {
                  const key = row.productCode
                  if (!acc[key]) {
                    acc[key] = {
                      productName: row.productName,
                      netMarginSum: 0,
                      revenueSum: 0,
                      netMarginContribution: 0,
                      count: 0,
                    }
                  }
                  acc[key].netMarginSum += Number(row.netMarginPercent)
                  acc[key].revenueSum += Number(row?.weeklySalesValue)
                  acc[key].netMarginContribution += Number(
                    row.netMarginContribution
                  )
                  acc[key].count += 1
                  return acc
                }, {})

                const rows = Object.values(grouped)
                  .map(r => ({ ...r, netMargin: r.revenueSum }))
                  .sort(
                    (a, b) => b.netMarginContribution - a.netMarginContribution
                  )

                const fmtGBP = v =>
                  (v < 0 ? "-£" : "£") +
                  Math.abs(v).toLocaleString("en-GB", {
                    maximumFractionDigits: 0,
                  })

                return (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: "#f8fafc",
                          borderBottom: "2px solid #e2e8f0",
                        }}
                      >
                        {[
                          "Product Name",
                          "Weekly Sales Value",
                          "Net Margin Contribution",
                        ].map((h, i) => (
                          <th
                            key={i}
                            style={{
                              padding: "10px 16px",
                              textAlign: i === 0 ? "left" : "right",
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        const color =
                          row.netMarginContribution >= 0 ? "#15803d" : "#be123c"

                        return (
                          <tr
                            key={i}
                            style={{
                              borderBottom: "1px solid #f1f5f9",
                              background: i % 2 === 0 ? "#fff" : "#fafbfc",
                            }}
                          >
                            <td
                              style={{
                                padding: "10px 16px",
                                color: "#1e293b",
                                fontWeight: 500,
                              }}
                            >
                              {row.productName}
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                textAlign: "right",
                                fontWeight: 700,
                                color,
                              }}
                            >
                              {fmtGBP(row.revenueSum)}
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                textAlign: "right",
                                fontWeight: 700,
                                color,
                              }}
                            >
                              {fmtGBP(row.netMarginContribution)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )
              })()}
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* ── Category / Sausage Category Drill-Down Modal ───────────── */}
      <Modal
        isOpen={catModal}
        toggle={() => setCatModal(false)}
        size="lg"
        centered
      >
        <ModalHeader
          toggle={() => setCatModal(false)}
          style={{ borderBottom: "1px solid #f1f5f9", padding: "14px 20px" }}
        >
          <span style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
            {catDrillTitle} — Customer Breakdown
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: 0 }}>
          {catDrillLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "48px",
              }}
            >
              <Spinner color="primary" />
            </div>
          ) : catDrillData.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px",
                color: "#94a3b8",
                fontSize: "14px",
              }}
            >
              No data available
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              {(() => {
                const fmtGBP = v =>
                  (v < 0 ? "-£" : "£") +
                  Math.abs(v).toLocaleString("en-GB", {
                    maximumFractionDigits: 0,
                  })

                const grouped = catDrillData.reduce((acc, row) => {
                  const key = row.customerCode || row.customerName
                  if (!acc[key]) {
                    acc[key] = {
                      customerName: row.customerName,
                      revenueSum: 0,
                      netMarginContribution: 0,
                    }
                  }
                  acc[key].revenueSum += Number(row.totalWeeklySalesValue || 0)
                  acc[key].netMarginContribution += Number(
                    row.totalNetMarginContribution || 0
                  )
                  return acc
                }, {})

                const rows = Object.values(grouped).sort(
                  (a, b) => b.revenueSum - a.revenueSum
                )

                return (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: "#f8fafc",
                          borderBottom: "2px solid #e2e8f0",
                        }}
                      >
                        {[
                          "Customer Name",
                          "Weekly Sales Value",
                          "Net Margin Contribution",
                        ].map((h, i) => (
                          <th
                            key={i}
                            style={{
                              padding: "10px 16px",
                              textAlign: i === 0 ? "left" : "right",
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        const color =
                          row.netMarginContribution >= 0 ? "#15803d" : "#be123c"
                        return (
                          <tr
                            key={i}
                            style={{
                              borderBottom: "1px solid #f1f5f9",
                              background: i % 2 === 0 ? "#fff" : "#fafbfc",
                            }}
                          >
                            <td
                              style={{
                                padding: "10px 16px",
                                color: "#1e293b",
                                fontWeight: 500,
                              }}
                            >
                              {row.customerName}
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                textAlign: "right",
                                fontWeight: 700,
                                color: "#1e293b",
                              }}
                            >
                              {fmtGBP(row.revenueSum)}
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                textAlign: "right",
                                fontWeight: 700,
                                color,
                              }}
                            >
                              {fmtGBP(row.netMarginContribution)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )
              })()}
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* ── Pipeline Tracker Drill-Down Modal ─────────────────────── */}
      <Modal
        isOpen={pipelineModal}
        toggle={() => setPipelineModal(false)}
        size="xl"
        centered
      >
        <ModalHeader
          toggle={() => setPipelineModal(false)}
          style={{ borderBottom: "1px solid #f1f5f9", padding: "14px 20px" }}
        >
          <span style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
            Pipeline Tracker — {pipelineTitle}
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: 0 }}>
          {pipelineLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}>
              <Spinner color="primary" />
            </div>
          ) : pipelineData.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px", color: "#94a3b8", fontSize: "14px" }}>
              No data available
            </div>
          ) : (
            <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
              {(() => {
                const COLS = [
                  { label: "Customer",              key: "customerName",        align: "left"  },
                  { label: "Product",               key: "productName",         align: "left"  },
                  { label: "Cost p/kg",             key: "costPerKg",           align: "right" },
                  { label: "SP p/kg",               key: "spPerKg",             align: "right" },
                  { label: "Weekly Sales Cases/Kg", key: "weeklySalesCasesKg",  align: "right" },
                  { label: "Net Margin £",          key: "netMarginPound",      align: "right" },
                  { label: "Net Margin %",          key: "netMarginPercent",    align: "right" },
                  { label: "CAPEX",                 key: "capex",               align: "right" },
                  { label: "ROI",                   key: "roi",                 align: "right" },
                  { label: "Approval",              key: "approval",            align: "center"},
                  { label: "Launch Date",           key: "launchDate",          align: "center"},
                ]
                return (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "13%" }} />
                      <col style={{ width: "8%"  }} />
                      <col style={{ width: "8%"  }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "9%"  }} />
                      <col style={{ width: "9%"  }} />
                      <col style={{ width: "7%"  }} />
                      <col style={{ width: "6%"  }} />
                      <col style={{ width: "9%"  }} />
                      <col style={{ width: "8%"  }} />
                    </colgroup>
                    <thead>
                      <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0", position: "sticky", top: 0, zIndex: 1 }}>
                        {COLS.map((col, i) => (
                          <th
                            key={i}
                            style={{
                              padding: "8px 8px",
                              textAlign: col.align,
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#64748b",
                              whiteSpace: "normal",
                              lineHeight: "1.3",
                            }}
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pipelineData.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                          {COLS.map((col, j) => (
                            <td
                              key={j}
                              style={{
                                padding: "7px 8px",
                                textAlign: col.align,
                                color: "#1e293b",
                                fontWeight: j <= 1 ? 600 : 400,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize : "12px"
                              }}
                              title={row[col.key] ?? ""}
                            >
                              {row[col.key] ?? "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              })()}
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default SalesRevenue
